import { useQuery, useMutation, useQueryClient } from 'react-query';

import api from '@src/utils/api';
import { CAPTIONS } from '@src/constants/endpoints';

export const CAPTIONS_QUERY_KEY = 'captions';
export const CAPTION_POLLING_INTERVAL_MS = 3000;

const isCaptionsDataProcessing = (data) => {
  const items = Array.isArray(data) ? data : [];
  return items.some((c) => c.status === 'pending' || c.status === 'processing');
};

export const isVersionsDataProcessing = (data) => {
  const items = Array.isArray(data) ? data : [];
  return items.some((v) => v.status === 'pending' || v.status === 'processing' || v.restoreStatus === 'restoring');
};

export const fetchCaptions = ({ queryKey }) => {
  const [, mediaId] = queryKey;
  return api.get(`${CAPTIONS}/media/${mediaId}`);
};

export const useFetchCaptions = (mediaId, options = {}) => {
  return useQuery([CAPTIONS_QUERY_KEY, mediaId], fetchCaptions, {
    enabled: !!mediaId,
    staleTime: 30000,
    ...options
  });
};

export const useCaptionPolling = (mediaId, options = {}) => {
  return useFetchCaptions(mediaId, {
    refetchInterval: (data) => (isCaptionsDataProcessing(data) ? CAPTION_POLLING_INTERVAL_MS : false),
    ...options
  });
};

const generateCaption = ({ mediaId, language }) =>
  api.post(CAPTIONS, { mediaId, language });

export const useGenerateCaption = () => {
  const queryClient = useQueryClient();
  return useMutation(generateCaption, {
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries([CAPTIONS_QUERY_KEY, variables.mediaId]);
      queryClient.invalidateQueries(['captionUsage']);
    }
  });
};

const fetchCaptionStatus = ({ queryKey }) => {
  const [, captionId] = queryKey;
  return api.get(`${CAPTIONS}/${captionId}/status`);
};

export const useFetchCaptionStatus = (captionId) => {
  return useQuery(['captionStatus', captionId], fetchCaptionStatus, {
    enabled: !!captionId
  });
};

const translateCaption = ({ captionId, targetLanguage }) =>
  api.post(`${CAPTIONS}/${captionId}/translate`, { targetLanguage });

export const useTranslateCaption = (mediaId) => {
  const queryClient = useQueryClient();
  return useMutation(translateCaption, {
    onSuccess: () => {
      queryClient.invalidateQueries([CAPTIONS_QUERY_KEY, mediaId]);
    }
  });
};

const fetchCaptionText = ({ queryKey }) => {
  const [, captionId] = queryKey;
  return api.get(`${CAPTIONS}/${captionId}/text`);
};

export const useFetchCaptionText = (captionId) => {
  return useQuery(['captionText', captionId], fetchCaptionText, {
    enabled: !!captionId,
    staleTime: 0
  });
};

const updateCaptionText = ({ captionId, text }) =>
  api.put(`${CAPTIONS}/${captionId}`, { text });

export const useUpdateCaptionText = (mediaId) => {
  const queryClient = useQueryClient();
  return useMutation(updateCaptionText, {
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries([CAPTIONS_QUERY_KEY, mediaId]);
      queryClient.invalidateQueries(['captionText', variables.captionId]);
    }
  });
};

const startBurnIn = ({ captionId, preset, highlightMode, wordsPerDisplay, crop, trimStart, trimEnd, watermark }) =>
  api.post(`${CAPTIONS}/${captionId}/burn-in`, { preset, highlightMode, wordsPerDisplay, crop, trimStart, trimEnd, watermark });

export const useStartBurnIn = (mediaId) => {
  const queryClient = useQueryClient();
  return useMutation(startBurnIn, {
    onSuccess: () => {
      queryClient.invalidateQueries([CAPTIONS_QUERY_KEY, mediaId]);
      queryClient.invalidateQueries(['captionVersions', mediaId]);
    }
  });
};

export const fetchMediaVersions = ({ queryKey }) => {
  const [, mediaId] = queryKey;
  return api.get(`${CAPTIONS}/media/${mediaId}/versions`);
};

export const useFetchMediaVersions = (mediaId, options = {}) => {
  return useQuery(['captionVersions', mediaId], fetchMediaVersions, {
    enabled: !!mediaId,
    staleTime: 30000,
    ...options
  });
};

const fetchCaptionUsage = () => api.get(`${CAPTIONS}/usage`);

export const useFetchCaptionUsage = (options = {}) => {
  return useQuery(['captionUsage'], fetchCaptionUsage, {
    staleTime: 60000,
    ...options
  });
};

const deleteBurnIn = (burnInId) => api.delete(`${CAPTIONS}/burn-in/${burnInId}`);

export const useDeleteBurnIn = (mediaId) => {
  const queryClient = useQueryClient();
  return useMutation(deleteBurnIn, {
    onSuccess: () => {
      queryClient.invalidateQueries(['captionVersions', mediaId]);
    }
  });
};

const restoreBurnIn = (burnInId) => api.post(`${CAPTIONS}/burn-in/${burnInId}/restore`);

export const useRestoreBurnIn = (mediaId) => {
  const queryClient = useQueryClient();
  return useMutation(restoreBurnIn, {
    onSuccess: () => {
      queryClient.invalidateQueries(['captionVersions', mediaId]);
    }
  });
};



// WEBPACK FOOTER //
// ./src/hooks/react-query/useCaptionsData.js